import { DownloadSimple, Gear } from "@phosphor-icons/react"
import { useState } from "react"
import { Button, IconButton } from "~/components/reusable/Button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/reusable/DropdownMenu"
import { Switch } from "~/components/reusable/Switch"
import { ShortcutTooltip } from "~/components/reusable/ShortcutTooltip"
import { Tooltip } from "~/components/reusable/Tooltip"
import { trackEvent } from "~/hooks/useTrackingService"
import { useDownloadSKUForecast, useDownloadVariantForecast } from "~/hooks/useDownloads"
import { useSkuStore } from "~/providers/sku-store-provider"
import { getStartOfCurrentWeekUTC } from "~/utils/datetime"
import { type Filter } from "~/utils/filter"
import { type AgGridReact } from "ag-grid-react"
import { addWeeks } from "date-fns"
import type { DocumentType } from "~/constants/documentType"
import { useExportConfig } from "~/hooks/useExportConfig"
import { useConfigureExportDialogStore } from "~/stores/configure-export-dialog-store"
import { useForecastValueMode } from "./ForecastValueModeButton"
import { Label } from "../reusable/Label"

type Granularity = "weekly" | "monthly"

interface Props {
  variants: { variantId: string }[]
  filters?: Filter[]
  gridRef: React.RefObject<Pick<AgGridReact, "api"> | null>
  endDate?: Date
  showSizeSplit?: boolean
  showMonthlyOption?: boolean
  showNetOptions?: boolean
  // Column IDs to force-include in the grid Excel export (e.g. hidden columns
  // that should always appear in the file).
  extraExportColumnKeys?: string[]
  // Sources for row-level comments. When set, the server includes a Comments column
  // in the SKU/Variant forecast exports, keyed by variantId (SKUs inherit their
  // parent variant's comments). Multiple sources are unioned (e.g. Demand Plan view
  // can include Supply Plan comments for the same collections).
  commentSources?: { documentType: DocumentType; documentIds: string[] }[]
  // When true, renders an icon-only IconButton trigger (no "Export" label).
  // Used inside the ViewConfiguration toolbar where space is tight.
  compact?: boolean
}

export default function ExportForecastPopover({
  variants,
  filters = [],
  gridRef,
  showSizeSplit,
  showMonthlyOption = false,
  showNetOptions = false,
  extraExportColumnKeys,
  commentSources,
  compact = false,
}: Props) {
  const openConfigDialog = useConfigureExportDialogStore((state) => state.openDialog)
  const [granularity, setGranularity] = useState<Granularity>("weekly")
  // Follow the table's gross/net mode: net pre-toggles the Net Forecast option.
  // A manual toggle in the popover sticks until the table mode changes again.
  const [valueMode] = useForecastValueMode()
  const netDefault = Boolean(showNetOptions) && valueMode === "net"
  const [includeNet, setIncludeNet] = useState(netDefault)
  const [prevNetDefault, setPrevNetDefault] = useState(netDefault)
  if (prevNetDefault !== netDefault) {
    setPrevNetDefault(netDefault)
    setIncludeNet(netDefault)
  }
  const [includeExpectedReturns, setIncludeExpectedReturns] = useState(false)
  const [exportConfig] = useExportConfig()
  const downloadVariantForecast = useDownloadVariantForecast()
  const downloadSKUForecast = useDownloadSKUForecast()
  const selectedWarehouseId = useSkuStore((state) => state.warehouseId)
  const selectedChannel = useSkuStore((state) => state.channel)
  const isDownloading = downloadVariantForecast.isPending || downloadSKUForecast.isPending
  const resolvedEndDate = addWeeks(getStartOfCurrentWeekUTC(), exportConfig.numberOfWeeks)

  return (
    <>
      <DropdownMenu>
        <ShortcutTooltip label="Export" disabled={!compact} disableHoverableContent>
          <DropdownMenuTrigger asChild>
            {compact ? (
              <IconButton
                size="sm"
                variant="neutral"
                loading={isDownloading}
                disabled={isDownloading}
                aria-label="Export"
              >
                <DownloadSimple />
              </IconButton>
            ) : (
              <Button size="sm" variant="neutral" loading={isDownloading} disabled={isDownloading}>
                <DownloadSimple />
                Export
              </Button>
            )}
          </DropdownMenuTrigger>
        </ShortcutTooltip>
        <DropdownMenuContent>
          {showMonthlyOption && (
            <>
              <Tooltip
                title="Demand granularity"
                description="Toggle between weekly and monthly aggregation in the exported file."
              >
                <div className="flex flex-col gap-2 p-2">
                  <Label>Granularity</Label>
                  <div
                    className="flex items-center justify-between gap-3 text-sm cursor-pointer"
                    onClick={() => setGranularity(granularity === "weekly" ? "monthly" : "weekly")}
                  >
                    <span>{granularity === "monthly" ? "Monthly" : "Weekly"}</span>
                    <Switch
                      checked={granularity === "monthly"}
                      onCheckedChange={(checked) => setGranularity(checked ? "monthly" : "weekly")}
                      aria-label="Toggle granularity between weekly and monthly"
                    />
                  </div>
                </div>
              </Tooltip>
              <DropdownMenuSeparator />
            </>
          )}
          {showNetOptions && (
            <>
              <Tooltip
                title="Net forecast"
                description="Adds a Net Forecast row per product: forecast × (1 - return rate)."
              >
                <div className="flex flex-col gap-2 p-2">
                  <div
                    className="flex items-center justify-between gap-3 text-sm cursor-pointer"
                    onClick={() => setIncludeNet(!includeNet)}
                  >
                    <Label>Net Forecast</Label>
                    <Switch
                      checked={includeNet}
                      onCheckedChange={setIncludeNet}
                      aria-label="Include net forecast in export"
                    />
                  </div>
                </div>
              </Tooltip>
              <Tooltip
                title="Expected returns"
                description="Adds an Expected Returns row per product with the projected resellable units coming back from customer returns."
              >
                <div className="flex flex-col gap-2 p-2">
                  <div
                    className="flex items-center justify-between gap-3 text-sm cursor-pointer"
                    onClick={() => setIncludeExpectedReturns(!includeExpectedReturns)}
                  >
                    <Label>Expected Returns</Label>
                    <Switch
                      checked={includeExpectedReturns}
                      onCheckedChange={setIncludeExpectedReturns}
                      aria-label="Include expected returns in export"
                    />
                  </div>
                </div>
              </Tooltip>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem
            onSelect={async (e) => {
              e.preventDefault()
              trackEvent("Download Forecasts", {
                Level: "sku",
                "Number Of Products": variants.length,
                "Filters Enabled": filters.length > 0,
                Granularity: granularity,
              })
              downloadSKUForecast.mutate({
                variantIds: variants.map((v) => v.variantId),
                startDate: getStartOfCurrentWeekUTC(),
                endDate: resolvedEndDate,
                warehouseId: selectedWarehouseId,
                channel: selectedChannel,
                decimals: exportConfig.numberOfDecimals,
                granularity,
                includeNet,
                includeExpectedReturns,
                commentSources,
              })
            }}
          >
            SKU Forecasts
          </DropdownMenuItem>
          {showSizeSplit && (
            <DropdownMenuItem
              onSelect={async (e) => {
                e.preventDefault()
                trackEvent("Download Forecasts", {
                  Level: "sku-with-size-split",
                  "Number Of Products": variants.length,
                  "Filters Enabled": filters.length > 0,
                  Granularity: granularity,
                })
                downloadSKUForecast.mutate({
                  variantIds: variants.map((v) => v.variantId),
                  startDate: getStartOfCurrentWeekUTC(),
                  endDate: resolvedEndDate,
                  warehouseId: selectedWarehouseId,
                  channel: selectedChannel,
                  decimals: exportConfig.numberOfDecimals,
                  includeSizeSplit: true,
                  granularity,
                  includeNet,
                  includeExpectedReturns,
                  commentSources,
                })
              }}
            >
              SKU Forecasts (with size split)
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onSelect={async (e) => {
              e.preventDefault()
              trackEvent("Download Forecasts", {
                Level: "variant",
                "Number Of Products": variants.length,
                "Filters Enabled": filters.length > 0,
                Granularity: granularity,
              })
              downloadVariantForecast.mutate({
                variantIds: variants.map((v) => v.variantId),
                startDate: getStartOfCurrentWeekUTC(),
                endDate: resolvedEndDate,
                warehouseId: selectedWarehouseId,
                channel: selectedChannel,
                decimals: exportConfig.numberOfDecimals,
                granularity,
                includeNet,
                includeExpectedReturns,
                commentSources,
              })
            }}
          >
            Variant Forecasts
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-end"
            onClick={() => {
              const api = gridRef.current?.api
              if (!api) return
              if (extraExportColumnKeys?.length) {
                const visibleKeys = api.getAllDisplayedColumns().map((c) => c.getColId())
                const extra = extraExportColumnKeys.filter((k) => !visibleKeys.includes(k))
                api.exportDataAsExcel({ columnKeys: [...visibleKeys, ...extra] })
              } else {
                api.exportDataAsExcel()
              }
            }}
          >
            Download variants
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => openConfigDialog()}>
            <Gear size="16px" />
            Configure exports...
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}
